import { MessageCircle, FolderOpen, FileText, PenTool, CalendarDays, Flame, ClipboardCheck, Target, Layers, Youtube, Languages, BookA, Brain } from 'lucide-react';

export type AppMode = 'funcionarios' | 'oposiciones' | 'oposiciones_c1' | 'oposiciones_guardia_civil' | 'oposiciones_iipp' | 'oposiciones_c2' | 'oposiciones_policia_nacional' | 'base_comun';

export interface TourStep {
  targetSelector: string;
  title: string;
  description: string;
  expandedDescription?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export interface ChecklistTask {
  id: string;
  title: string;
  icon: any;
  navigateTo: string;
}

export const TOUR_STEPS: Record<AppMode, TourStep[]> = {
  funcionarios: [
    {
      targetSelector: '[data-tour="chat-input"]',
      title: 'Tu asistente jurídico',
      description: 'Aquí puedes hacer consultas jurídicas sobre derecho administrativo, hacienda local y más.',
      expandedDescription: 'Escribe tu pregunta como si hablaras con un compañero experto. Puedes adjuntar documentos PDF para que los analice y puedes hacer preguntas de seguimiento en la misma conversación.',
      position: 'top',
    },
    {
      targetSelector: '[data-tour="sidebar"]',
      title: 'Organiza tus conversaciones',
      description: 'Todas tus consultas se guardan aquí. Crea carpetas para organizarlas por tema.',
      expandedDescription: 'Puedes fijar las conversaciones más importantes, buscar por contenido y mover chats entre carpetas arrastrándolos. Las carpetas te permiten separar por áreas: urbanismo, contratación, hacienda, etc.',
      position: 'right',
    },
    {
      targetSelector: '[data-tour="documents"]',
      title: 'Mis Documentos',
      description: 'Accede a los documentos que has guardado desde tus conversaciones.',
      expandedDescription: 'Cuando el asistente genera un informe, dictamen o cualquier documento, puedes guardarlo aquí para consultarlo más tarde o exportarlo en Word/PDF.',
      position: 'right',
    },
    {
      targetSelector: '[data-tour="redactor"]',
      title: 'Modo Redactor',
      description: 'Genera documentos jurídicos completos con ayuda de la IA.',
      expandedDescription: 'Elige entre plantillas predefinidas (decretos, informes, providencias...) o describe el documento que necesitas. La IA te guiará paso a paso recogiendo los datos necesarios.',
      position: 'right',
    },
    {
      targetSelector: '[data-tour="deadlines"]',
      title: 'Control de plazos',
      description: 'Gestiona tus plazos administrativos y recibe recordatorios.',
      expandedDescription: 'Configura plazos con días hábiles o naturales, vincula documentos y recibe notificaciones antes del vencimiento. Puedes importar plazos desde documentos escaneados.',
      position: 'right',
    },
    {
      targetSelector: '',
      title: 'Sobre la Inteligencia Artificial en MUNILEX',
      description: 'MUNILEX usa IA para asistirte en consultas jurídicas, generar documentos y analizar expedientes. Todo el contenido generado es orientativo.',
      expandedDescription: 'La IA analiza tus consultas y documentos para ofrecer respuestas fundamentadas en la normativa española.\n\nRecuerda: la información es orientativa. Verifica siempre con fuentes oficiales (BOE, CENDOJ, DGT, TEAC) antes de tomar decisiones profesionales.\n\nPuedes reportar errores usando el botón de reporte.',
      position: 'bottom',
    },
  ],
  oposiciones: [
    {
      targetSelector: '[data-tour="stats-cards"]',
      title: 'Tu panel de estudio',
      description: 'Aquí ves tu racha de estudio, media de tests y flashcards pendientes.',
      expandedDescription: 'La racha se actualiza cada día que estudias. Intenta mantenerla para crear un hábito constante. La media de tests te ayuda a ver tu progreso global.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-test"]',
      title: 'Tests del temario',
      description: 'Genera tests por tema o en modo examen oficial (100 preguntas, 2 horas).',
      expandedDescription: 'Puedes elegir temas específicos, nivel de dificultad y número de preguntas. El modo examen oficial simula las condiciones reales con penalización de 1/3 por error.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-caso"]',
      title: 'Casos prácticos',
      description: 'Practica supuestos prácticos evaluados por IA.',
      expandedDescription: 'La IA genera supuestos realistas del temario y evalúa tu respuesta con criterios de corrección objetivos, dándote feedback detallado para mejorar.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-flashcards"]',
      title: 'Flashcards inteligentes',
      description: 'Repasa con repetición espaciada para memorizar a largo plazo.',
      expandedDescription: 'El sistema SM-2 programa automáticamente las revisiones. Las tarjetas que más te cuestan se repiten más a menudo, las que dominas aparecen menos.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-chat"]',
      title: 'Chat especializado',
      description: 'Resuelve dudas sobre el temario con el asistente de oposiciones.',
      expandedDescription: 'Pregunta sobre cualquier tema del programa. El asistente conoce la normativa actualizada y puede explicarte conceptos complejos de forma clara.',
      position: 'bottom',
    },
    {
      targetSelector: '',
      title: 'Sobre la Inteligencia Artificial en MUNILEX',
      description: 'MUNILEX usa IA para generar tests, flashcards, evaluar supuestos prácticos y resolver dudas del temario. Todo el contenido generado es orientativo.',
      expandedDescription: 'La IA analiza tu temario, genera preguntas adaptadas a tu nivel y corrige tus respuestas con criterios objetivos.\n\nRecuerda: la información es orientativa. Verifica siempre con fuentes oficiales antes de preparar tu oposición.\n\nPuedes reportar errores usando el botón de reporte.',
      position: 'bottom',
    },
  ],
  oposiciones_c1: [
    {
      targetSelector: '[data-tour="stats-cards"]',
      title: 'Panel de seguimiento C1',
      description: 'Tu racha, media de aciertos y flashcards pendientes de hoy.',
      expandedDescription: 'Mantener una racha constante es clave para la preparación. Revisa tus estadísticas cada día para ajustar tu plan de estudio.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-test"]',
      title: 'Tests del temario C1',
      description: 'Tests del Cuerpo General Administrativo del Estado.',
      expandedDescription: 'Genera tests por bloques temáticos o aleatorios. Incluye Constitución, Derecho Administrativo, Gestión de Personal y Gestión Financiera.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-caso"]',
      title: 'Supuestos prácticos',
      description: 'Supuestos prácticos en formato test oficial.',
      expandedDescription: 'Practica con supuestos que simulan el segundo ejercicio de la oposición. La IA evalúa tus respuestas y te da feedback personalizado.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-flashcards"]',
      title: 'Flashcards C1',
      description: 'Repaso con repetición espaciada del temario C1.',
      expandedDescription: 'Genera flashcards de cualquier tema y el algoritmo SM-2 te las reprograma automáticamente según tu nivel de dominio.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-recursos"]',
      title: 'Recursos verificados',
      description: 'Busca legislación y materiales verificados para tu preparación.',
      expandedDescription: 'Accede a vídeos, artículos y normativa relevante para cada tema. Los recursos están verificados y clasificados por relevancia.',
      position: 'bottom',
    },
    {
      targetSelector: '',
      title: 'Sobre la Inteligencia Artificial en MUNILEX',
      description: 'MUNILEX usa IA para generar tests, flashcards, evaluar supuestos prácticos y resolver dudas del temario C1. Todo el contenido generado es orientativo.',
      expandedDescription: 'La IA analiza tu temario, genera preguntas adaptadas a tu nivel y corrige tus respuestas con criterios objetivos.\n\nRecuerda: la información es orientativa. Verifica siempre con fuentes oficiales antes de preparar tu oposición.\n\nPuedes reportar errores usando el botón de reporte.',
      position: 'bottom',
    },
  ],
  oposiciones_guardia_civil: [
    {
      targetSelector: '[data-tour="stats-cards"]',
      title: 'Tu panel Guardia Civil',
      description: 'Racha de estudio, media de tests y flashcards pendientes hoy.',
      expandedDescription: 'El panel reúne todas tus métricas clave: días consecutivos estudiando, porcentaje medio de aciertos en tests y tarjetas que toca repasar hoy según el algoritmo SM-2.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-test"]',
      title: 'Tests del temario oficial',
      description: 'Genera tests por tema o simulacros del temario de Guardia Civil.',
      expandedDescription: 'Practica con preguntas tipo test del temario oficial: Constitución, Derechos Humanos, Unión Europea, Organización del Estado, Derecho Penal, Protección Civil y Guardia Civil. Configura dificultad y número de preguntas.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-caso"]',
      title: 'Supuestos prácticos',
      description: 'Resuelve supuestos prácticos evaluados por IA.',
      expandedDescription: 'Casos realistas de actuación: identificación, atestados, intervención en accidentes, infracciones administrativas. La IA evalúa tu respuesta con criterios objetivos.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-flashcards"]',
      title: 'Flashcards de repaso',
      description: 'Memoriza a largo plazo con el sistema SM-2.',
      expandedDescription: 'El algoritmo programa cada tarjeta según lo bien que la recuerdas. Las que te cuestan vuelven antes; las dominadas se espacian más en el tiempo.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-ingles"]',
      title: 'Inglés B1 ⭐',
      description: 'Módulo exclusivo de inglés nivel B1 para Guardia Civil.',
      expandedDescription: 'Práctica adaptada a la prueba oficial de inglés: comprensión escrita, gramática y vocabulario al nivel exigido en la oposición.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-lengua"]',
      title: 'Lengua castellana ⭐',
      description: 'Práctica de la prueba de Lengua específica de Guardia Civil.',
      expandedDescription: 'Ortografía, gramática, léxico y comprensión lectora al nivel y formato de la prueba oficial. Ejercicios tipo test corregidos al instante.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-psicotecnico"]',
      title: 'Psicotécnicos ⭐',
      description: 'Series, razonamiento abstracto y pruebas visuales.',
      expandedDescription: 'Entrena los tres bloques clásicos: aptitud verbal, numérica y de razonamiento. Cronómetro y feedback inmediato para ganar velocidad y precisión.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-recursos"]',
      title: 'Recursos verificados',
      description: 'Vídeos, normativa y material de academias verificadas.',
      expandedDescription: 'Acceso a recursos curados: legislación actualizada, vídeos formativos y materiales de referencia para cada tema del programa.',
      position: 'bottom',
    },
    {
      targetSelector: '',
      title: 'Sobre la Inteligencia Artificial en MUNILEX',
      description: 'MUNILEX usa IA para generar tests, flashcards, evaluar supuestos prácticos y resolver dudas del temario. Todo el contenido generado es orientativo.',
      expandedDescription: 'La IA analiza tu temario, genera preguntas adaptadas a tu nivel y corrige tus respuestas con criterios objetivos.\n\nRecuerda: la información es orientativa. Verifica siempre con fuentes oficiales antes de preparar tu oposición.\n\nPuedes reportar errores usando el botón de reporte.',
      position: 'bottom',
    },
  ],
  oposiciones_iipp: [
    {
      targetSelector: '[data-tour="stats-cards"]',
      title: 'Tu panel Ayudante II.PP.',
      description: 'Racha, media de aciertos y flashcards pendientes hoy.',
      expandedDescription: 'Sigue tu evolución diaria. Mantener una racha constante mejora la retención y la curva de aprendizaje a largo plazo.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-test"]',
      title: 'Tests del temario',
      description: 'Tests del temario oficial de Ayudante de Instituciones Penitenciarias.',
      expandedDescription: 'Modo examen oficial con 150 preguntas y 120 minutos, con penalización por error. También tests por tema con número y dificultad personalizables.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-caso"]',
      title: 'Supuestos prácticos',
      description: 'Casos prácticos del ámbito penitenciario evaluados por IA.',
      expandedDescription: 'Supuestos realistas: regímenes de vida, incidencias, comunicaciones, traslados. La IA evalúa con criterios objetivos y te da feedback detallado.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-flashcards"]',
      title: 'Flashcards de repaso',
      description: 'Repaso espaciado del temario penitenciario.',
      expandedDescription: 'Genera flashcards por tema y deja que el algoritmo te programe los repasos en el momento óptimo para fijar conceptos a largo plazo.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-chat"]',
      title: 'Chat especializado',
      description: 'Resuelve dudas con un asistente experto en II.PP.',
      expandedDescription: 'Pregunta sobre LOGP, Reglamento Penitenciario, normativa de funcionarios o cualquier punto del temario. Respuestas con fundamento normativo.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-recursos"]',
      title: 'Recursos verificados',
      description: 'Vídeos y materiales curados para tu preparación.',
      expandedDescription: 'Recursos clasificados por tema, con normativa actualizada y materiales de academias y profesionales verificados.',
      position: 'bottom',
    },
    {
      targetSelector: '',
      title: 'Sobre la Inteligencia Artificial en MUNILEX',
      description: 'MUNILEX usa IA para generar tests, flashcards, evaluar supuestos prácticos y resolver dudas de Instituciones Penitenciarias. Todo el contenido generado es orientativo.',
      expandedDescription: 'La IA analiza tu temario, genera preguntas adaptadas a tu nivel y corrige tus respuestas con criterios objetivos.\n\nRecuerda: la información es orientativa. Verifica siempre con fuentes oficiales antes de preparar tu oposición.\n\nPuedes reportar errores usando el botón de reporte.',
      position: 'bottom',
    },
  ],
  oposiciones_c2: [
    {
      targetSelector: '[data-tour="stats-cards"]',
      title: 'Tu panel C2 Auxiliar Estado',
      description: 'Racha, media de aciertos y flashcards pendientes hoy.',
      expandedDescription: 'Métricas de seguimiento diario: constancia, rendimiento medio en tests y carga de repaso. Ajusta tu plan según los datos.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-test"]',
      title: 'Tests del temario C2',
      description: 'Tests del Cuerpo General Auxiliar de la Administración del Estado.',
      expandedDescription: 'Simulacro oficial con 60 preguntas y 90 minutos, penalización 1/3. También tests por tema configurables. Cubre Constitución, Régimen Jurídico, AGE, Personal y atención al ciudadano.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-caso"]',
      title: 'Supuestos prácticos',
      description: 'Supuestos en formato test propios del C2.',
      expandedDescription: 'Práctica del segundo ejercicio en formato tipo test sobre supuestos administrativos: registro, atención e información, tramitación y archivo.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-flashcards"]',
      title: 'Flashcards de repaso',
      description: 'Repaso espaciado del temario C2.',
      expandedDescription: 'Crea flashcards de cualquier tema. El sistema te las reprograma automáticamente según tu nivel de dominio.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-chat"]',
      title: 'Chat especializado',
      description: 'Resuelve dudas del temario C2 con la IA.',
      expandedDescription: 'Pregunta sobre Ley 39/2015, Ley 40/2015, EBEP, Constitución o cualquier punto del programa. Explicaciones claras con base normativa.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-recursos"]',
      title: 'Recursos verificados',
      description: 'Vídeos, normativa y materiales curados para C2.',
      expandedDescription: 'Acceso a contenido verificado y clasificado por tema: legislación actualizada, vídeos explicativos y materiales de referencia.',
      position: 'bottom',
    },
    {
      targetSelector: '',
      title: 'Sobre la Inteligencia Artificial en MUNILEX',
      description: 'MUNILEX usa IA para generar tests, flashcards, evaluar supuestos prácticos y resolver dudas del temario C2. Todo el contenido generado es orientativo.',
      expandedDescription: 'La IA analiza tu temario, genera preguntas adaptadas a tu nivel y corrige tus respuestas con criterios objetivos.\n\nRecuerda: la información es orientativa. Verifica siempre con fuentes oficiales antes de preparar tu oposición.\n\nPuedes reportar errores usando el botón de reporte.',
      position: 'bottom',
    },
  ],
  oposiciones_policia_nacional: [
    {
      targetSelector: '[data-tour="stats-cards"]',
      title: 'Tu panel Policía Nacional',
      description: 'Racha de estudio, media de aciertos y flashcards pendientes.',
      expandedDescription: 'Controla tu progreso diario: días consecutivos estudiando, tu porcentaje medio de aciertos y las tarjetas que debes repasar hoy para no olvidar nada.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-test"]',
      title: 'Tests del temario oficial',
      description: 'Genera tests por temas o simulacros de examen oficial.',
      expandedDescription: 'Practica con preguntas del temario de Policía Nacional: Constitución, Derecho Penal, Seguridad Ciudadana, Policía Científica y más. Modo examen: 100 preguntas, 50 minutos.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-caso"]',
      title: 'Supuestos prácticos',
      description: 'Resuelve casos de actuación policial evaluados por IA.',
      expandedDescription: 'Simulacros de situaciones reales que podrías encontrar en el examen o en el servicio. La IA analiza tu respuesta y te da feedback técnico.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-ingles"]',
      title: 'Prueba de Inglés',
      description: 'Entrena la prueba de idiomas al nivel exigido.',
      expandedDescription: 'Ejercicios de gramática, vocabulario y comprensión lectora adaptados al formato oficial de la Policía Nacional.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-lengua"]',
      title: 'Ortografía y Lengua',
      description: 'Mejora tu nivel de español y comprensión verbal.',
      expandedDescription: 'Ejercicios de ortografía, sinónimos, antónimos y comprensión de textos para asegurar tu aprobado en esta sección.',
      position: 'bottom',
    },
    {
      targetSelector: '[data-tour="nav-psicotecnico"]',
      title: 'Psicotécnicos',
      description: 'Entrenamiento de aptitud verbal, numérica y razonamiento.',
      expandedDescription: 'Mejora tu agilidad mental y velocidad de respuesta con series, problemas lógicos y razonamiento espacial.',
      position: 'bottom',
    },
    {
      targetSelector: '',
      title: 'Sobre la Inteligencia Artificial en MUNILEX',
      description: 'MUNILEX usa IA para generar tests, flashcards, evaluar supuestos prácticos y resolver dudas del temario. Todo el contenido generado es orientativo.',
      expandedDescription: 'La IA analiza tu temario, genera preguntas adaptadas a tu nivel y corrige tus respuestas con criterios objetivos.\n\nRecuerda: la información es orientativa. Verifica siempre con fuentes oficiales antes de preparar tu oposición.\n\nPuedes reportar errores usando el botón de reporte.',
      position: 'bottom',
    },
  ],
};

const gcTasks = (base: string) => [
  { id: 'first_test', title: 'Completa tu primer test', icon: ClipboardCheck, navigateTo: `${base}/test` },
  { id: 'generate_flashcards', title: 'Genera flashcards de un tema', icon: Layers, navigateTo: `${base}/flashcards` },
  { id: 'first_case', title: 'Haz un supuesto práctico', icon: Target, navigateTo: `${base}/caso` },
  { id: 'practice_english', title: 'Practica Inglés B1', icon: Languages, navigateTo: `${base}/ingles` },
  { id: 'practice_language', title: 'Practica Lengua', icon: BookA, navigateTo: `${base}/lengua` },
  { id: 'practice_psychotechnical', title: 'Resuelve un psicotécnico', icon: Brain, navigateTo: `${base}/psicotecnico` },
  { id: 'first_chat', title: 'Usa el chat para resolver una duda', icon: MessageCircle, navigateTo: `${base}/chat` },
];

const oposiTasks = (base: string) => [
  { id: 'first_test', title: 'Completa tu primer test', icon: ClipboardCheck, navigateTo: `${base}/test` },
  { id: 'generate_flashcards', title: 'Genera flashcards de un tema', icon: Layers, navigateTo: `${base}/flashcards` },
  { id: 'review_flashcards', title: 'Revisa tus flashcards pendientes', icon: Layers, navigateTo: `${base}/flashcards` },
  { id: 'first_case', title: 'Haz un supuesto práctico', icon: Target, navigateTo: `${base}/caso` },
  { id: 'first_chat', title: 'Usa el chat para resolver una duda', icon: MessageCircle, navigateTo: `${base}/chat` },
];

export const CHECKLIST_TASKS: Record<AppMode, ChecklistTask[]> = {
  funcionarios: [
    { id: 'first_chat', title: 'Haz tu primera consulta jurídica', icon: MessageCircle, navigateTo: '/app/chat' },
    { id: 'save_message', title: 'Guarda un mensaje importante', icon: FileText, navigateTo: '/app/saved' },
    { id: 'create_folder', title: 'Crea una carpeta para tus chats', icon: FolderOpen, navigateTo: '/app/chat' },
    { id: 'generate_document', title: 'Genera un documento con el Redactor', icon: PenTool, navigateTo: '/app/redactor' },
  ],
  oposiciones: oposiTasks('/app/oposiciones'),
  oposiciones_c1: oposiTasks('/app/oposiciones-c1'),
  oposiciones_guardia_civil: gcTasks('/app/guardia-civil'),
  oposiciones_iipp: oposiTasks('/app/oposiciones-iipp'),
  oposiciones_c2: oposiTasks('/app/oposiciones-c2'),
  oposiciones_policia_nacional: gcTasks('/app/policia-nacional'),
};
